// Planroom's own 2D primitives. App code depends on these models instead of the
// licensed editor's classes; any engine's results must satisfy them structurally.

export type VecLike = { x: number; y: number; z?: number };
export type BoxLike = { x: number; y: number; w: number; h: number };

export interface VecModel extends VecLike {
  clone(): VecModel;
}

export interface BoxModel extends BoxLike {
  readonly minX: number;
  readonly minY: number;
  readonly maxX: number;
  readonly maxY: number;
  readonly midX: number;
  readonly midY: number;
  readonly width: number;
  readonly height: number;
  readonly center: VecModel;
  readonly point: VecModel;
  readonly corners: VecModel[];
  clone(): BoxModel;
  contains(other: BoxLike): boolean;
  collides(other: BoxLike): boolean;
  includes(other: BoxLike): boolean;
  containsPoint(point: VecLike, margin?: number): boolean;
}

/** A shape's page transform: an affine matrix. */
export interface MatModel {
  a: number;
  b: number;
  c: number;
  d: number;
  e: number;
  f: number;
  applyToPoint(point: VecLike): VecModel;
  rotation(): number;
}

/** The parts of a shape's geometry the app reads. */
export interface GeometryModel {
  readonly bounds: BoxModel;
  readonly center: VecModel;
  readonly vertices: VecModel[];
  readonly isClosed: boolean;
  hitTestPoint(point: VecLike, margin?: number, hitInside?: boolean): boolean;
  distanceToPoint(point: VecLike, hitInside?: boolean): number;
}

export class Vec implements VecModel {
  constructor(
    public x = 0,
    public y = 0,
    public z = 1,
  ) {}

  clone() {
    return new Vec(this.x, this.y, this.z);
  }
  add(v: VecLike) {
    return new Vec(this.x + v.x, this.y + v.y);
  }
  sub(v: VecLike) {
    return new Vec(this.x - v.x, this.y - v.y);
  }
  mul(t: number) {
    return new Vec(this.x * t, this.y * t);
  }
  len() {
    return Math.hypot(this.x, this.y);
  }
  dist(v: VecLike) {
    return Vec.Dist(this, v);
  }
  toJson() {
    return { x: this.x, y: this.y, z: this.z };
  }

  static From(v: VecLike) {
    return new Vec(v.x, v.y, v.z ?? 1);
  }
  static Add(a: VecLike, b: VecLike) {
    return new Vec(a.x + b.x, a.y + b.y);
  }
  static Sub(a: VecLike, b: VecLike) {
    return new Vec(a.x - b.x, a.y - b.y);
  }
  static Dist(a: VecLike, b: VecLike) {
    return Math.hypot(a.x - b.x, a.y - b.y);
  }
  static Lrp(a: VecLike, b: VecLike, t: number) {
    return new Vec(a.x + (b.x - a.x) * t, a.y + (b.y - a.y) * t);
  }
}

export class Box implements BoxModel {
  constructor(
    public x = 0,
    public y = 0,
    public w = 0,
    public h = 0,
  ) {}

  get minX() {
    return this.x;
  }
  get minY() {
    return this.y;
  }
  get maxX() {
    return this.x + this.w;
  }
  get maxY() {
    return this.y + this.h;
  }
  get midX() {
    return this.x + this.w / 2;
  }
  get midY() {
    return this.y + this.h / 2;
  }
  get width() {
    return this.w;
  }
  get height() {
    return this.h;
  }
  get center() {
    return new Vec(this.midX, this.midY);
  }
  get point() {
    return new Vec(this.x, this.y);
  }
  get corners() {
    return [
      new Vec(this.minX, this.minY),
      new Vec(this.maxX, this.minY),
      new Vec(this.maxX, this.maxY),
      new Vec(this.minX, this.maxY),
    ];
  }

  clone() {
    return new Box(this.x, this.y, this.w, this.h);
  }
  contains(o: BoxLike) {
    return Box.Contains(this, o);
  }
  collides(o: BoxLike) {
    return Box.Collides(this, o);
  }
  includes(o: BoxLike) {
    return Box.Collides(this, o) || Box.Contains(this, o);
  }
  containsPoint(p: VecLike, margin = 0) {
    return (
      p.x >= this.minX - margin &&
      p.x <= this.maxX + margin &&
      p.y >= this.minY - margin &&
      p.y <= this.maxY + margin
    );
  }
  expandBy(n: number) {
    return new Box(this.x - n, this.y - n, this.w + n * 2, this.h + n * 2);
  }
  union(o: BoxLike) {
    return Box.Common([this, o]);
  }
  toJson(): BoxLike {
    return { x: this.x, y: this.y, w: this.w, h: this.h };
  }

  static From(b: BoxLike) {
    return new Box(b.x, b.y, b.w, b.h);
  }
  static FromPoints(points: VecLike[]) {
    if (!points.length) return new Box();
    let minX = Infinity,
      minY = Infinity,
      maxX = -Infinity,
      maxY = -Infinity;
    for (const p of points) {
      minX = Math.min(minX, p.x);
      minY = Math.min(minY, p.y);
      maxX = Math.max(maxX, p.x);
      maxY = Math.max(maxY, p.y);
    }
    return new Box(minX, minY, maxX - minX, maxY - minY);
  }
  /** The smallest box containing every box. */
  static Common(boxes: BoxLike[]) {
    return Box.FromPoints(
      boxes.flatMap((b) => [
        { x: b.x, y: b.y },
        { x: b.x + b.w, y: b.y + b.h },
      ]),
    );
  }
  /** Whether two boxes overlap (edges touching count). */
  static Collides(a: BoxLike, b: BoxLike) {
    return !(
      a.x + a.w < b.x ||
      a.x > b.x + b.w ||
      a.y + a.h < b.y ||
      a.y > b.y + b.h
    );
  }
  /** Whether `b` lies entirely inside `a`. */
  static Contains(a: BoxLike, b: BoxLike) {
    return (
      a.x < b.x && a.y < b.y && a.x + a.w > b.x + b.w && a.y + a.h > b.y + b.h
    );
  }
}
